import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
  withStyles,
} from '@material-ui/core';
import { withTranslation } from 'react-i18next';
import { compose } from 'recompose';
import styles from './styles';
import PropTypes from 'prop-types';
import TemplateComparisonTable from '../Tables/TemplateComparisonTable';
import { displayModes } from 'services/enums/displayModes';
import BaseTemplateComparisonMap from 'components/Maps/BaseMaps/BaseTemplateComparisonMap';
import classNames from 'classnames';
import TemplateStopIconLegend from 'components/Maps/MapComponents/TemplateStopIconLegend';
import useTableConfig from 'hooks/useTableConfig';
import { getTableKey } from 'services/util/tableConfigHelper';
import { TableKind } from 'services/enums/TableKind';
import Columns from '../Tables/columns';
import { TableSettings } from 'components';
import DefaultConfig from '../Tables/config';
import { CheckCircleOutline, RemoveCircleOutline } from '@material-ui/icons';
import TemplateAggregationCard from "../TemplateAggregationCard";

function TemplateComparison(props) {
  const {
    classes,
    className,
    oldTemplates,
    optimizedTemplates,
    displayMode,
    selectedOldTemplateIds,
    setSelectedOldTemplateIds,
    selectedOptimizedTemplateIds,
    setSelectedOptimizedTemplateIds,
    t,
  } = props;

  const [greyOutOldTemplates, setGreyOutOldTemplates] = useState(true);

  function onSelectOldTemplate(template) {
    const selected =
      selectedOldTemplateIds.findIndex((id) => id === template.id) >= 0;
    if (selected) {
      setSelectedOldTemplateIds(
        selectedOldTemplateIds.filter((id) => id !== template.id)
      );
    } else {
      setSelectedOldTemplateIds([...selectedOldTemplateIds, template.id]);
    }
  }

  function onSelectOptimizedTemplate(template) {
    const selected =
      selectedOptimizedTemplateIds.findIndex((id) => id === template.tempId) >= 0;
    if (selected) {
      setSelectedOptimizedTemplateIds(
        selectedOptimizedTemplateIds.filter((id) => id !== template.tempId)
      );
    } else {
      setSelectedOptimizedTemplateIds([
        ...selectedOptimizedTemplateIds,
        template.tempId,
      ]);
    }
  }

  const {
    config: configOld,
    columnConfigs: columnConfigsOld,
    updateConfig: updateConfigOld,
  } = useTableConfig(
    DefaultConfig,
    displayMode,
    getTableKey(TableKind.TemplateStopComparisonOldTable, displayMode)
  );

  const {
    config: configOptimized,
    columnConfigs: columnConfigsOptimized,
    updateConfig: updateConfigOptimized,
  } = useTableConfig(
    DefaultConfig,
    displayMode,
    getTableKey(TableKind.TemplateStopComparisonOptimizedTable, displayMode)
  );

  const selectAllOldTemplates = useCallback(() => {
    const allTemplateIds = oldTemplates.map((template) => template.id);
    setSelectedOldTemplateIds(allTemplateIds);
  }, [oldTemplates, setSelectedOldTemplateIds]);

  const selectAllOptimizedTemplates = useCallback(() => {
    const allTemplateIds = optimizedTemplates.map((template) => template.tempId);
    setSelectedOptimizedTemplateIds(allTemplateIds);
  }, [optimizedTemplates, setSelectedOptimizedTemplateIds]);

  function deselectAllOldTemplates() {
    setSelectedOldTemplateIds([]);
  }

  function deselectAllOptimizedTemplates() {
    setSelectedOptimizedTemplateIds([]);
  }

  useEffect(() => {
    selectAllOldTemplates();
    selectAllOptimizedTemplates();
  }, [selectAllOldTemplates, selectAllOptimizedTemplates]);

  return (
    <div className={classes.root}>
      <Grid className={className} container spacing={1}>
        <Grid item lg={12} sm={12} xl={6} xs={12}>
          <div className={classNames(classes.mapLegend, classes.topSpacer)}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={greyOutOldTemplates}
                  color="secondary"
                  onChange={(event) =>
                    setGreyOutOldTemplates(event.target.checked)
                  }
                />
              }
              label={t('greyOutOldTemplates')}
            />
            <TemplateStopIconLegend />
          </div>
          <BaseTemplateComparisonMap
            className={classes.gridItem}
            oldTemplates={oldTemplates.filter(
              (template) => selectedOldTemplateIds.indexOf(template.id) >= 0
            )}
            optimizedTemplates={optimizedTemplates.filter(
              (template) =>
                selectedOptimizedTemplateIds.indexOf(template.tempId) >= 0
            )}
            greyOutOldTemplates={greyOutOldTemplates}
          />
        </Grid>
        <Grid item lg={12} sm={12} xl={6} xs={12}>
          <div className={classes.topSpacer} />
          <div className={classes.gridItem}>
            <div className={classes.tableLegend}>
              <Typography className={classes.title} variant="h4">
                {t('oldTemplates')}
              </Typography>
              <TableSettings
                buttonOptic
                className={classes.tableSettings}
                columns={Columns}
                tableConfig={configOld}
                updateConfig={updateConfigOld}
                useNamesAsTranslateString
              />
            </div>
            <TemplateAggregationCard
              templates={oldTemplates}
            />
            <TemplateComparisonTable
              className={classes.templateTable}
              displayMode={displayMode}
              onSelectTemplate={(template) => onSelectOldTemplate(template)}
              selectedTemplateIds={selectedOldTemplateIds}
              templates={oldTemplates}
              columnConfigs={columnConfigsOld}
              idKey="id"
            />
            <div className={classes.buttonContainer}>
              <Button
                color="primary"
                variant="contained"
                onClick={selectAllOldTemplates}
                className={classes.button}
                startIcon={<CheckCircleOutline />}
              >
                {t('selectAll')}
              </Button>
              <Button
                color="primary"
                variant="contained"
                onClick={deselectAllOldTemplates}
                className={classes.button}
                startIcon={<RemoveCircleOutline />}
              >
                {t('deselectAll')}
              </Button>
            </div>
            <Box sx={{ height: '20px' }} />
            <div className={classes.tableLegend}>
              <Typography className={classes.title} variant="h4">
                {t('optimizedTemplates')}
              </Typography>
              <TableSettings
                buttonOptic
                className={classes.tableSettings}
                columns={Columns}
                tableConfig={configOptimized}
                updateConfig={updateConfigOptimized}
                useNamesAsTranslateString
              />
            </div>
            <TemplateAggregationCard
              templates={optimizedTemplates}
            />
            <TemplateComparisonTable
              className={classes.templateTable}
              displayMode={displayMode}
              onSelectTemplate={(template) =>
                onSelectOptimizedTemplate(template)
              }
              selectedTemplateIds={selectedOptimizedTemplateIds}
              templates={optimizedTemplates}
              columnConfigs={columnConfigsOptimized}
              idKey="tempId"
            />
            <div className={classes.buttonContainer}>
              <Button
                color="primary"
                variant="contained"
                onClick={selectAllOptimizedTemplates}
                className={classes.button}
                startIcon={<CheckCircleOutline />}
              >
                {t('selectAll')}
              </Button>
              <Button
                color="primary"
                variant="contained"
                onClick={deselectAllOptimizedTemplates}
                className={classes.button}
                startIcon={<RemoveCircleOutline />}
              >
                {t('deselectAll')}
              </Button>
            </div>
          </div>
        </Grid>
      </Grid>
    </div>
  );
}

TemplateComparison.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  displayMode: PropTypes.oneOf(Object.values(displayModes)).isRequired,
  oldTemplates: PropTypes.array.isRequired,
  optimizedTemplates: PropTypes.array.isRequired,
  selectedOldTemplateIds: PropTypes.array.isRequired,
  setSelectedOldTemplateIds: PropTypes.func.isRequired,
  selectedOptimizedTemplateIds: PropTypes.array.isRequired,
  setSelectedOptimizedTemplateIds: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

export default compose(
  withStyles(styles),
  withTranslation()
)(TemplateComparison);
